/**
 * Merchant display labels + select options (ported from cpanel
 * `features/merchants/model/constants.ts` — logic 1:1).
 */
import type {
  ConfiguredMerchantVertical,
  EmployeeRole,
  LegalForm,
} from '../model/schema';

/**
 * Display labels resolve through the `merchants` i18n namespace (repo
 * `labelKey` precedent): the maps below carry stable keys, the Russian
 * source-of-truth strings live in the per-language `merchants.json` locale
 * files, and call sites render `t(KEY_MAP[value])`.
 */
export const VERTICAL_LABEL_KEYS: Record<string, string> = {
  MERCHANT_VERTICAL_UNSPECIFIED: 'merchants:verticals.unspecified',
  MERCHANT_VERTICAL_EATS: 'merchants:verticals.eats',
  MERCHANT_VERTICAL_PHARMA: 'merchants:verticals.pharma',
  MERCHANT_VERTICAL_DELIVERY: 'merchants:verticals.delivery',
};

export const MERCHANT_VERTICAL_DELIVERY = 'MERCHANT_VERTICAL_DELIVERY' as const;
export const MERCHANT_VERTICAL_PHARMA = 'MERCHANT_VERTICAL_PHARMA' as const;
export const MERCHANT_VERTICAL_EATS = 'MERCHANT_VERTICAL_EATS' as const;

/**
 * Russian labels stay for the delivery-verification diagnostics
 * (`delivery-sync.ts` embeds them in mismatch toasts); UI renders
 * `LEGAL_FORM_LABEL_KEYS` through i18n.
 */
export const LEGAL_FORM_LABELS: Record<string, string> = {
  LEGAL_FORM_UNSPECIFIED: 'Не указана',
  LEGAL_FORM_INDIVIDUAL: 'Физическое лицо',
  LEGAL_FORM_COMPANY: 'Юридическое лицо',
};

export const LEGAL_FORM_LABEL_KEYS: Record<string, string> = {
  LEGAL_FORM_UNSPECIFIED: 'merchants:legalForm.unspecified',
  LEGAL_FORM_INDIVIDUAL: 'merchants:legalForm.individual',
  LEGAL_FORM_COMPANY: 'merchants:legalForm.company',
};

export const ROLE_LABEL_KEYS: Record<string, string> = {
  'merchant-admin': 'merchants:roles.merchant-admin',
  manager: 'merchants:roles.manager',
  cashier: 'merchants:roles.cashier',
};

/** Real verticals, i.e. everything except the UNSPECIFIED default. */
export const VERTICAL_OPTIONS: { value: ConfiguredMerchantVertical; labelKey: string }[] =
  [
    {
      value: 'MERCHANT_VERTICAL_EATS',
      labelKey: VERTICAL_LABEL_KEYS.MERCHANT_VERTICAL_EATS ?? '',
    },
    {
      value: 'MERCHANT_VERTICAL_PHARMA',
      labelKey: VERTICAL_LABEL_KEYS.MERCHANT_VERTICAL_PHARMA ?? '',
    },
    {
      value: MERCHANT_VERTICAL_DELIVERY,
      labelKey: VERTICAL_LABEL_KEYS[MERCHANT_VERTICAL_DELIVERY] ?? '',
    },
  ];

export const LEGAL_FORM_OPTIONS: { value: LegalForm; labelKey: string }[] = [
  {
    value: 'LEGAL_FORM_INDIVIDUAL',
    labelKey: LEGAL_FORM_LABEL_KEYS.LEGAL_FORM_INDIVIDUAL ?? '',
  },
  {
    value: 'LEGAL_FORM_COMPANY',
    labelKey: LEGAL_FORM_LABEL_KEYS.LEGAL_FORM_COMPANY ?? '',
  },
];

/**
 * A merchant employee may hold any of these, but the FIRST admin of a brand is
 * always merchant-admin and is not chosen — see `CreateMerchantPayload`.
 */
export const EMPLOYEE_ROLE_OPTIONS: { value: EmployeeRole; labelKey: string }[] = [
  { value: 'merchant-admin', labelKey: ROLE_LABEL_KEYS['merchant-admin'] ?? '' },
  { value: 'manager', labelKey: ROLE_LABEL_KEYS.manager ?? '' },
  { value: 'cashier', labelKey: ROLE_LABEL_KEYS.cashier ?? '' },
];

export const EATS_MENU_SOURCE_OPTIONS = [
  { value: 'internal', label: 'Internal' },
  { value: 'delever', label: 'Delever' },
  { value: 'iko', label: 'Iko' },
  { value: 'rahmat', label: 'Rahmat' },
];

export const EATS_DELIVERY_PROVIDER_OPTIONS = [
  { value: 'noor', label: 'Noor' },
  { value: 'delever', label: 'Delever' },
  { value: 'iko', label: 'Iko' },
  { value: 'rahmat', label: 'Rahmat' },
];

/**
 * Display label for an integration option token from
 * `GET /api/v1/merchants/integration-options` — known tokens keep their
 * canonical labels, unknown future tokens fall back to a capitalized token
 * instead of an empty label.
 */
export function toIntegrationOptionLabel(value: string): string {
  const known = [...EATS_MENU_SOURCE_OPTIONS, ...EATS_DELIVERY_PROVIDER_OPTIONS].find(
    (o) => o.value === value,
  );
  if (known) return known.label;
  return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export const PHARMA_AUTH_KIND_OPTIONS: { value: string; nameKey: string }[] = [
  { value: 'AUTH_KIND_NONE', nameKey: 'merchants:pharma.authKinds.none' },
  { value: 'AUTH_KIND_API_KEY', nameKey: 'merchants:pharma.authKinds.apiKey' },
  { value: 'AUTH_KIND_BASIC', nameKey: 'merchants:pharma.authKinds.basic' },
];

export const PHARMA_CRM_TYPE_OPTIONS: { value: string; name: string }[] = [
  { value: 'pharma_cosmos', name: 'Pharma Cosmos' },
  { value: '1c_pharma', name: '1C Pharma' },
];

export const PHARMA_ENDPOINT_FIELDS = [
  { key: 'ENDPOINT_KEY_SHOPS', labelKey: 'merchants:pharma.endpoints.shops' },
  { key: 'ENDPOINT_KEY_PRODUCTS', labelKey: 'merchants:pharma.endpoints.products' },
  { key: 'ENDPOINT_KEY_STOCK', labelKey: 'merchants:pharma.endpoints.stock' },
  { key: 'ENDPOINT_KEY_IMAGES', labelKey: 'merchants:pharma.endpoints.images' },
  {
    key: 'ENDPOINT_KEY_MARKUP_CODES',
    labelKey: 'merchants:pharma.endpoints.markupCodes',
  },
  {
    key: 'ENDPOINT_KEY_ORDER_CREATE',
    labelKey: 'merchants:pharma.endpoints.orderCreate',
  },
  {
    key: 'ENDPOINT_KEY_ORDER_CANCEL',
    labelKey: 'merchants:pharma.endpoints.orderCancel',
  },
  {
    key: 'ENDPOINT_KEY_ORDER_ACCEPT_COURIER',
    labelKey: 'merchants:pharma.endpoints.orderAcceptCourier',
  },
] as const;

/** Hour values; labels resolve with pluralization at the call site. */
export const PHARMA_SYNC_INTERVAL_HOURS = ['2', '3', '4', '5', '6', '7', '8'] as const;

export const PHARMA_SYNC_INTERVAL_FIELDS = [
  {
    key: 'SYNC_INTERVAL_KEY_SYNC_SHOPS_INTERVAL',
    labelKey: 'merchants:pharma.intervals.shops',
  },
  {
    key: 'SYNC_INTERVAL_KEY_SYNC_PRODUCTS_INTERVAL',
    labelKey: 'merchants:pharma.intervals.products',
  },
] as const;

/**
 * Searchable merchant-list columns. The `search_type` select on /merchants
 * offers these property names; option labels reuse the table column labels
 * (`merchants:table.*`) so the two can never drift apart.
 */
export const MERCHANT_SEARCH_TYPES = ['business_name', 'inn', 'phone'] as const;
export type MerchantSearchType = (typeof MERCHANT_SEARCH_TYPES)[number];

export const DEFAULT_MERCHANT_SEARCH_TYPE: MerchantSearchType = 'business_name';

export const MERCHANT_SEARCH_TYPE_LABEL_KEYS: Record<string, string> = {
  business_name: 'merchants:table.name',
  inn: 'merchants:table.inn',
  phone: 'merchants:table.phone',
};

/**
 * Merchant-list status filter (`GET /api/v1/admin/merchants/filter`). Omitted
 * — everything except archived.
 */
export const MERCHANT_STATUSES = ['active', 'paused', 'suspended', 'archived'] as const;
export type MerchantStatus = (typeof MERCHANT_STATUSES)[number];

/**
 * Status options offered by the /merchants filter. `suspended` stays a valid
 * backend status (URL + wire query) but is not offered in the UI.
 */
export const MERCHANT_STATUS_FILTER_OPTIONS = ['active', 'archived'] as const;

export const MERCHANT_STATUS_LABEL_KEYS: Record<string, string> = {
  active: 'merchants:list.statuses.active',
  paused: 'merchants:list.statuses.paused',
  suspended: 'merchants:list.statuses.suspended',
  archived: 'merchants:list.statuses.archived',
};

/** Default page size for the merchants list. */
export const PAGE_LIMIT = 20;

/** Default page size mirrors the backend's own default (`page_size: 15`). */
export const INTEGRATION_TOKEN_PAGE_LIMIT = 10;

export const INTEGRATION_TOKEN_SCOPES: { value: string; labelKey: string }[] = [
  { value: 'all', labelKey: 'merchants:tokens.scopes.all' },
  { value: 'business', labelKey: 'merchants:tokens.scopes.business' },
  { value: 'individual', labelKey: 'merchants:tokens.scopes.individual' },
];

export const INTEGRATION_TOKEN_STATUSES: { value: string; labelKey: string }[] = [
  { value: 'not_migrated', labelKey: 'merchants:tokens.statuses.not_migrated' },
  { value: 'issued_not_used', labelKey: 'merchants:tokens.statuses.issued_not_used' },
  { value: 'active', labelKey: 'merchants:tokens.statuses.active' },
];

export const INTEGRATION_TOKEN_STATUS_LABEL_KEYS: Record<string, string> =
  Object.fromEntries(
    INTEGRATION_TOKEN_STATUSES.map((option) => [option.value, option.labelKey]),
  );

export const INTEGRATION_TOKEN_SCOPE_LABEL_KEYS: Record<string, string> =
  Object.fromEntries(
    INTEGRATION_TOKEN_SCOPES.map((option) => [option.value, option.labelKey]),
  );

export interface CredentialFieldConfig {
  key: 'client_id' | 'client_secret' | 'api_key' | 'app_id';
  labelKey: string;
  placeholderKey: string;
  type: 'text' | 'password';
}

export interface ProviderIntegrationConfig {
  showCredentials: boolean;
  credentialsTitleKey?: string;
  fields: CredentialFieldConfig[];
}

export const DELIVERY_PROVIDER_CONFIGS: Record<string, ProviderIntegrationConfig> = {
  delever: {
    showCredentials: true,
    credentialsTitleKey: 'merchants:eats.deleverCredentialsTitle',
    fields: [
      {
        key: 'client_id',
        labelKey: 'merchants:eats.loginLabel',
        placeholderKey: 'merchants:eats.loginPlaceholder',
        type: 'text',
      },
      {
        key: 'client_secret',
        labelKey: 'merchants:eats.passwordLabel',
        placeholderKey: 'merchants:eats.passwordPlaceholder',
        type: 'password',
      },
    ],
  },
  rahmat: {
    showCredentials: true,
    credentialsTitleKey: 'merchants:eats.rahmatCredentialsTitle',
    fields: [
      {
        key: 'client_id',
        labelKey: 'merchants:eats.loginLabel',
        placeholderKey: 'merchants:eats.loginPlaceholder',
        type: 'text',
      },
      {
        key: 'client_secret',
        labelKey: 'merchants:eats.passwordLabel',
        placeholderKey: 'merchants:eats.passwordPlaceholder',
        type: 'password',
      },
    ],
  },
  iko: {
    showCredentials: true,
    credentialsTitleKey: 'merchants:eats.ikoCredentialsTitle',
    fields: [
      {
        key: 'api_key',
        labelKey: 'merchants:eats.apiKeyLabel',
        placeholderKey: 'merchants:eats.apiKeyPlaceholder',
        type: 'text',
      },
      {
        key: 'app_id',
        labelKey: 'merchants:eats.appIdLabel',
        placeholderKey: 'merchants:eats.appIdPlaceholder',
        type: 'text',
      },
      {
        key: 'client_secret',
        labelKey: 'merchants:eats.passwordLabel',
        placeholderKey: 'merchants:eats.passwordPlaceholder',
        type: 'password',
      },
    ],
  },
};

export function getProviderIntegrationConfig(
  provider: string | undefined,
): ProviderIntegrationConfig {
  if (!provider) {
    return { showCredentials: false, fields: [] };
  }
  return DELIVERY_PROVIDER_CONFIGS[provider] ?? { showCredentials: false, fields: [] };
}
